import { getAuthBaseUrl, getOAuthAppId, getOAuthClientId, getOAuthRedirectUri } from '@deriv/shared';

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

const generateCodeVerifier = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

const PKCE_VERIFIER_KEY = 'oauth_code_verifier';
const PKCE_EXPIRY_KEY = 'oauth_code_verifier_timestamp';
const PKCE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export const storePKCEVerifier = (verifier: string): void => {
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(PKCE_EXPIRY_KEY, String(Date.now()));
};

export const getPKCEVerifier = (): string | null => {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const timestamp = Number(sessionStorage.getItem(PKCE_EXPIRY_KEY) ?? 0);
    if (!verifier || Date.now() - timestamp > PKCE_TTL_MS) {
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_EXPIRY_KEY);
        return null;
    }
    return verifier;
};

export const clearPKCEVerifier = (): void => {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_EXPIRY_KEY);
};

// ---------------------------------------------------------------------------
// OAuth URL generation
// ---------------------------------------------------------------------------

export const generateOAuthURL = async (): Promise<string> => {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    storePKCEVerifier(verifier);

    const csrf_token = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16))));
    sessionStorage.setItem('oauth_csrf_token', csrf_token);

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: getOAuthClientId(),
        redirect_uri: getOAuthRedirectUri(),
        scope: 'trade',
        state: csrf_token,
        code_challenge: challenge,
        code_challenge_method: 'S256',
    });
    const oauth_app_id = getOAuthAppId();
    if (oauth_app_id) params.set('app_id', oauth_app_id);

    return `${getAuthBaseUrl()}/oauth2/auth?${params}`;
};

// ---------------------------------------------------------------------------
// Token exchange
// ---------------------------------------------------------------------------

export const exchangeCodeForToken = async (
    code: string
): Promise<{ access_token: string; refresh_token?: string; expires_in?: number }> => {
    const verifier = getPKCEVerifier();
    if (!verifier) throw new Error('PKCE code verifier not found or expired — restart login flow');

    const response = await fetch(`${getAuthBaseUrl()}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            code,
            redirect_uri: getOAuthRedirectUri(),
            client_id: getOAuthClientId(),
            code_verifier: verifier,
        }),
    });

    if (!response.ok) throw new Error(`Token exchange failed: ${response.status}`);

    const data = await response.json();
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[OAuth] Token exchange response:', {
            scope: data.scope,
            token_type: data.token_type,
            expires_in: data.expires_in,
        });
    }
    if (!data.access_token)
        throw new Error(`Token exchange succeeded but no access_token in response: ${JSON.stringify(data)}`);
    clearPKCEVerifier();
    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    return data;
};

// ---------------------------------------------------------------------------
// Token storage
// Tokens are dual-written to localStorage AND sessionStorage. Mirrors the
// newapiOnly pattern: sessionStorage is fast and tab-scoped, localStorage
// survives tab close, browser restart, and (crucially) mobile-Chrome tab
// eviction under memory pressure — without it, returning users keep getting
// silently logged out on mobile. Reads prefer sessionStorage (fresh) and fall
// back to localStorage (cached).
//
// CRITICAL: same-origin iframe shares localStorage AND sessionStorage with the
// parent (newapiOnly). The parent ALSO writes `auth_info` — its payload has the
// refresh_token, ours does not (parent only sends access_token via postMessage).
// If we wrote to the unprefixed `auth_info` key we would overwrite parent's
// refresh_token with undefined, breaking parent's silent refresh flow and
// causing the account-switcher loader to spin forever / random hard logouts.
// In embed mode we use a `dtrader_` prefix; standalone uses the original keys
// for backwards-compat with prior dtrader deploys.
// ---------------------------------------------------------------------------

const is_embedded = (): boolean => typeof window !== 'undefined' && window !== window.top;
const scoped = (key: string): string => (is_embedded() ? `dtrader_${key}` : key);

const AUTH_INFO_KEY = 'auth_info';

export const storeTokens = (access_token: string, refresh_token?: string, expires_in?: number): void => {
    const payload = JSON.stringify({
        access_token,
        refresh_token,
        expires_at: expires_in ? Date.now() + expires_in * 1000 : null,
    });
    const key = scoped(AUTH_INFO_KEY);
    sessionStorage.setItem(key, payload);
    localStorage.setItem(key, payload);
};

export const getStoredToken = (): string | null => {
    try {
        const key = scoped(AUTH_INFO_KEY);
        // Embed mode: also fall back to parent's unprefixed `auth_info` key —
        // when the iframe first loads (before any ACCOUNT_CHANGED message), our
        // prefixed key is empty but the parent already has its token in the
        // unprefixed key. Reading it lets us start authorising immediately
        // without waiting for the postMessage handshake.
        const raw =
            sessionStorage.getItem(key) ??
            localStorage.getItem(key) ??
            (is_embedded()
                ? sessionStorage.getItem(AUTH_INFO_KEY) ?? localStorage.getItem(AUTH_INFO_KEY)
                : null);
        const info = JSON.parse(raw ?? 'null');
        if (!info) return null;
        if (info.expires_at && Date.now() >= info.expires_at) {
            return null;
        }
        return info.access_token ?? null;
    } catch {
        return null;
    }
};

export const clearTokens = (): void => {
    const key = scoped(AUTH_INFO_KEY);
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
};

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------

export const refreshAccessToken = async (): Promise<string> => {
    // Embed mode: the parent owns the refresh_token. Calling the OAuth refresh
    // endpoint from here would use a token we don't actually have. Instead, signal
    // the parent so it runs the refresh and re-pushes ACCOUNT_CHANGED.
    if (is_embedded()) {
        try {
            window.parent.postMessage({ type: 'DTRADER_TOKEN_INVALID' }, '*');
        } catch {
            /* ignore */
        }
        throw new Error('Embedded mode: refresh deferred to parent');
    }
    const key = scoped(AUTH_INFO_KEY);
    const info = JSON.parse(sessionStorage.getItem(key) ?? localStorage.getItem(key) ?? 'null');
    if (!info?.refresh_token) throw new Error('No refresh token stored');

    const response = await fetch(`${getAuthBaseUrl()}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: info.refresh_token,
            client_id: getOAuthClientId(),
        }),
    });

    if (!response.ok) throw new Error(`Token refresh failed: ${response.status}`);
    const data = await response.json();
    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    return data.access_token;
};
